import React, { useMemo, useState } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Grid,
  Skeleton,
  Typography,
} from "@mui/material";
import Pagination from "./Pagination";
import { useNavigate } from "react-router-dom";
import { useOrganisation } from "../../../hooks/useOrganisation";
import Button from "../Button";

const ListTable = ({
  data = [],
  columns,
  isLoading,
  isInitialLoading = false,
  totalPage,
  setCurrentPage,
  currentPage,
  showPageSize = false,
  pageSize = 10,
  setPageSize,
  totalItems = 0,
  // New props for customizable widths
  enableColumnResizing = true,
  initialColumnWidths = {},
  onColumnWidthChange,
}) => {
  const navigate = useNavigate();
  const companyName = localStorage.getItem("companyName");
  const { primaryColor } = useOrganisation();

  // State for column widths - can be controlled externally or internally
  const [columnWidths, setColumnWidths] = useState(() => {
    const defaultWidths = {
      article: 220,
      topic: 100,
      author: 120,
      dateCreated: 150,
      scheduledDate: 150,
      featured: 110,
      status: 140,
      actions: 150,
    };
    return { ...defaultWidths, ...initialColumnWidths };
  });

  const handleColumnWidthUpdate = (columnId, newWidth) => {
    const updatedWidths = { ...columnWidths, [columnId]: newWidth };
    setColumnWidths(updatedWidths);

    // Call external callback if provided
    if (onColumnWidthChange) {
      onColumnWidthChange(updatedWidths);
    }
  };

  const columnSizingInfo = useMemo(() => {
    const columnSizing = {};

    columns.forEach((column) => {
      const id = column.accessorKey || column.id;
      const width = columnWidths[id] || 150;

      columnSizing[id] = {
        minWidth: Math.max(50, width * 0.5), // Minimum 50px or 50% of width
        width: width,
        maxWidth: width * 3, // Allow up to 3x the set width
      };
    });

    return columnSizing;
  }, [columns, columnWidths]);

  // Enhanced columns with dynamic sizing
  const enhancedColumns = useMemo(() => {
    return columns.map((column) => ({
      ...column,
      size: columnSizingInfo[column.accessorKey || column.id]?.width || 150,
      minSize:
        columnSizingInfo[column.accessorKey || column.id]?.minWidth || 50,
      maxSize:
        columnSizingInfo[column.accessorKey || column.id]?.maxWidth || 500,
    }));
  }, [columns, columnSizingInfo]);

  const table = useReactTable({
    data,
    columns: enhancedColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: enableColumnResizing,
    onColumnSizingChange: (updater) => {
      // Handle TanStack Table's built-in resizing
      if (typeof updater === "function") {
        const newSizing = updater({});
        Object.entries(newSizing).forEach(([columnId, size]) => {
          handleColumnWidthUpdate(columnId, size);
        });
      }
    },
  });

  const SkeletonRows = ({ rowCount = 5 }) => {
    return Array.from({ length: rowCount }, (_, index) => (
      <TableRow key={`skeleton-${index}`}>
        {columns.map((column) => {
          const id = column.accessorKey || column.id;
          const sizing = columnSizingInfo[id];

          return (
            <TableCell
              key={`skeleton-cell-${index}-${id}`}
              sx={{
                minWidth: `${sizing?.minWidth}px`,
                width: `${sizing?.width}px`,
                maxWidth: `${sizing?.maxWidth}px`,
                padding: "8px 16px",
                boxSizing: "border-box",
              }}
            >
              <Skeleton
                variant="text"
                height={20}
                sx={{
                  width: id === "actions" ? "80%" : "100%",
                  borderRadius: 1,
                }}
              />
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  const LoadingOverlay = () => (
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        borderRadius: 1,
      }}
    >
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={40} sx={{ color: primaryColor }} />
        <Typography variant="body2" sx={{ mt: 2, color: "text.secondary" }}>
          Loading data...
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <TableContainer
          component={Paper}
          sx={{ marginBottom: 2, overflowX: "auto" }}
        >
          <Table
            sx={{ tableLayout: "fixed", width: "100%" }}
            aria-label="content table"
          >
            <TableHead sx={{ backgroundColor: "black" }}>
              <TableRow>
                {table.getHeaderGroups().map((headerGroup) =>
                  headerGroup.headers.map((header) => {
                    const id = header.column.id;
                    const sizing = columnSizingInfo[id];

                    return (
                      <TableCell
                        key={header.id}
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          minWidth: `${sizing?.minWidth}px`,
                          width: `${sizing?.width}px`,
                          maxWidth: `${sizing?.maxWidth}px`,
                          padding: "8px 16px",
                          boxSizing: "border-box",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </Box>

                        {/* TanStack Table's built-in resize handle */}
                        {enableColumnResizing && (
                          <Box
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            sx={{
                              position: "absolute",
                              right: 0,
                              top: 15,
                              height: "50%",
                              width: "1px",
                              backgroundColor: "gray",
                              cursor: "col-resize",
                              userSelect: "none",
                              touchAction: "none",
                              "&:hover": {
                                backgroundColor: "white",
                              },
                            }}
                          />
                        )}
                      </TableCell>
                    );
                  })
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isInitialLoading ? (
                <SkeletonRows rowCount={pageSize || 10} />
              ) : isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <CircularProgress sx={{ color: primaryColor }} />
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "text.secondary" }}
                    >
                      Loading...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                table?.getRowModel().rows.map((row, i) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                      opacity: isLoading ? 0.5 : 1,
                      transition: "opacity 0.2s ease",
                    }}
                  >
                    {row?.getVisibleCells().map((cell) => {
                      const id = cell.column.id;
                      const sizing = columnSizingInfo[id];
                      const isActionsColumn = id === "actions";

                      return (
                        <TableCell
                          key={cell.id}
                          sx={{
                            minWidth: `${sizing?.minWidth}px`,
                            width: `${sizing?.width}px`,
                            maxWidth: `${sizing?.maxWidth}px`,
                            padding: "8px 16px",
                            boxSizing: "border-box",
                            whiteSpace: "nowrap",
                            overflow: isActionsColumn ? "visible" : "hidden",
                            textOverflow: "ellipsis",
                            position: isActionsColumn ? "relative" : "static",
                            zIndex: isActionsColumn ? 10 : "auto",
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    align="center"
                    sx={{ py: 6 }}
                  >
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      No data available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      There are no records to display at this time.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isLoading && data.length > 0 && <LoadingOverlay />}
      </Box>

      {totalPage > 1 && (
        <Pagination
          count={totalPage}
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          showPageSize={showPageSize}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalItems={totalItems}
          disabled={isLoading}
        />
      )}

      <Grid container justifyContent="center" sx={{ marginTop: 2 }}>
        <Button
          text="Back to dashboard"
          onClick={() => navigate(`/${companyName}/dashboard`)}
          disabled={isInitialLoading}
        />
      </Grid>
    </>
  );
};

export default ListTable;
